/* Product page and quick add (§11.1).
   <product-info>  variant change → ?option_values=…&section_id=… → swap [data-product-part] regions,
                   update ?variant= and the gallery; quantity steppers; sticky info only while it fits.
   <arrives-by>    today + lead time as a date range (Intl.DateTimeFormat).
   <sticky-atc>    shows when the product form scrolls above the screen, hides over the footer.
   <unit-toggle>   cm / in for the dimension drawing, remembered in localStorage. */
(() => {
  if (customElements.get('product-info')) return;

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.onChange = (event) => {
        if (event.target.matches('[data-variant-picker] input')) this.selectVariant(event.target);
      };
      this.onClick = (event) => {
        const step = event.target.closest('.pf [data-qty-step]');
        if (!step) return;
        const input = step.parentElement.querySelector('input[type="number"]');
        const min = Number(input.min) || 1;
        input.value = Math.max(min, (Number(input.value) || min) + Number(step.dataset.qtyStep));
      };
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.watchInfoHeight();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    async selectVariant(input) {
      // Combined listings: the value belongs to a different product.
      if (input.dataset.productUrl) {
        window.location.href = input.dataset.productUrl;
        return;
      }
      const ids = [...this.querySelectorAll('[data-variant-picker] input:checked')].map((el) => el.dataset.optionValueId);
      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.searchParams.set('option_values', ids.join(','));
      url.searchParams.set('section_id', this.dataset.sectionId);

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const focusedId = document.activeElement && document.activeElement.id;
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        doc.querySelectorAll('[data-product-part]').forEach((fresh) => {
          const current = this.querySelector(`[data-product-part="${fresh.dataset.productPart}"]`);
          if (current) current.innerHTML = fresh.innerHTML;
        });
        if (focusedId) document.getElementById(focusedId)?.focus();

        const data = JSON.parse(this.querySelector('[data-selected-variant]')?.textContent || '{}');
        if (data.id && this.hasAttribute('data-update-url')) {
          const next = new URL(window.location.href);
          next.searchParams.set('variant', data.id);
          window.history.replaceState(window.history.state, '', next);
        }
        if (data.mediaId) this.querySelector('media-gallery')?.showMedia(data.mediaId);
        this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: data }));
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    // A sticky column taller than the screen hides its own end, so it only sticks while it fits.
    watchInfoHeight() {
      const info = this.querySelector('[data-sticky-info]');
      if (!info) return;
      this.resizeObserver = new ResizeObserver(() => {
        info.classList.toggle('is-static', info.offsetHeight > window.innerHeight - 120);
      });
      this.resizeObserver.observe(info);
    }
  }

  class ArrivesBy extends HTMLElement {
    connectedCallback() {
      const weeks = Number(this.dataset.weeks);
      const text = this.querySelector('[data-arrives-text]');
      if (!weeks || !text || !this.dataset.template) return;
      const day = 86400000;
      const from = new Date(Date.now() + weeks * 7 * day);
      const to = new Date(from.getTime() + 7 * day);
      const format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { day: 'numeric', month: 'short' });
      const range = format.formatRange ? format.formatRange(from, to) : `${format.format(from)} – ${format.format(to)}`;
      text.innerHTML = this.dataset.template.replace('[range]', range);
    }
  }

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      if (!this.form) return;
      this.footer = document.querySelector('site-footer');
      this.formAbove = false;
      this.footerVisible = false;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.form) this.formAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          else this.footerVisible = entry.isIntersecting;
        });
        this.toggle(this.formAbove && !this.footerVisible);
      });
      this.observer.observe(this.form);
      if (this.footer) this.observer.observe(this.footer);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    toggle(show) {
      this.classList.toggle('is-visible', show);
      this.toggleAttribute('inert', !show);
    }
  }

  class UnitToggle extends HTMLElement {
    connectedCallback() {
      let unit = 'cm';
      try {
        unit = localStorage.getItem('theme:unit') || 'cm';
      } catch (error) {
        /* storage unavailable */
      }
      this.set(unit);
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-unit-set]');
        if (!button) return;
        this.set(button.dataset.unitSet);
        try {
          localStorage.setItem('theme:unit', button.dataset.unitSet);
        } catch (error) {
          /* storage unavailable */
        }
      });
    }

    set(unit) {
      this.dataset.unit = unit;
      this.querySelectorAll('[data-unit-set]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.unitSet === unit));
      });
    }
  }

  customElements.define('product-info', ProductInfo);
  customElements.define('arrives-by', ArrivesBy);
  customElements.define('sticky-atc', StickyAtc);
  customElements.define('unit-toggle', UnitToggle);
})();
